import { useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface TransitionState {
  landerLoading?: string;
}

let tokenCounter = 0;

/**
 * A loader is a transition on the current step, never a page of its own:
 * - the loader is a history entry on the same URL, replaced by the next step when the work succeeds,
 *   so Back from the next step never lands on a loader;
 * - Back during the loader returns to the step and the move forward is dropped;
 * - after a refresh a leftover loader entry just shows the step.
 */
export function useStepTransition() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = (location.state as TransitionState | null)?.landerLoading;
  const ownTokensRef = useRef(new Set<string>());
  const currentTokenRef = useRef(token);
  currentTokenRef.current = token;

  const isLoading = Boolean(token && ownTokensRef.current.has(token));

  const transition = useCallback(
    async (work: () => Promise<boolean>, to: string) => {
      const loaderToken = `t${++tokenCounter}`;
      ownTokensRef.current.add(loaderToken);
      navigate(
        { pathname: location.pathname, search: location.search },
        { state: { landerLoading: loaderToken } }
      );
      currentTokenRef.current = loaderToken;

      let succeeded = false;
      try {
        succeeded = await work();
      } catch {
        succeeded = false;
      }

      if (currentTokenRef.current !== loaderToken) return false;
      if (succeeded) {
        navigate(to, { replace: true });
      } else {
        navigate(-1);
      }
      return succeeded;
    },
    [location.pathname, location.search, navigate]
  );

  return { isLoading, transition };
}
